/**
 * Bulb upsell for Lampetorvet (Prestige 4.13).
 *
 * PDP: when the customer adds a lamp (Prestige dispatches "product:added" after its own /cart/add.js call),
 *      the ticked bulb(s) in the "Husk pæren" block are added as separate line items, then "cart:refresh"
 *      re-renders the drawer so lamp + bulb show together. Bulb quantity = light sources x lamp quantity.
 * Cart: the "Læg i kurven" button in the "Husk pæren" card adds the bulb, then the drawer re-renders
 *      (or the cart page reloads).
 * Every upsold bulb carries the hidden line item property _kilde = "pdp" | "kurv" for measurement.
 *
 * "Køb nu" (Shopify's unbranded dynamic checkout button): when a bulb is ticked we take over the click, add lamp + bulb
 *      to the cart and go straight to /checkout. Without a ticked bulb the button is untouched. A branded button
 *      (Shop Pay / PayPal, rendered in an iframe) cannot be intercepted; the store shows the unbranded one.
 * On a slow connection Prestige's own drawer render can land after ours; the drawer then shows the lamp only until
 * the next refresh (the cart itself is correct).
 */
(function () {
  'use strict';

  function cartAddUrl() {
    return ((window.routes && window.routes.cartAddUrl) || '/cart/add') + '.js';
  }

  function addItems(items) {
    return fetch(cartAddUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: items })
    }).then(function (response) {
      if (!response.ok) {
        return response.json().catch(function () { return {}; }).then(function (body) {
          throw new Error((body && (body.description || body.message)) || 'Kunne ikke lægge pæren i kurven.');
        });
      }
      return response.json();
    });
  }

  /* Prestige's CartSection listens on document.documentElement (see custom.js), so the event must bubble from <html>. */
  function refreshCart() {
    if (window.theme && window.theme.pageType === 'cart') {
      window.location.reload();
      return;
    }
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  function formatMoney(cents) {
    var hasOre = cents % 100 !== 0;
    var amount = cents / 100;
    try {
      return new Intl.NumberFormat('da-DK', {
        minimumFractionDigits: hasOre ? 2 : 0,
        maximumFractionDigits: hasOre ? 2 : 0
      }).format(amount) + ' kr';
    } catch (e) {
      return (hasOre ? amount.toFixed(2).replace('.', ',') : Math.round(amount)) + ' kr';
    }
  }

  function showError(container, message) {
    if (!container) { return; }
    var existing = container.querySelector('.BulbUpsell__Error');
    if (existing) { existing.remove(); }
    var el = document.createElement('p');
    el.className = 'BulbUpsell__Error';
    el.textContent = message;
    container.appendChild(el);
    setTimeout(function () { el.remove(); }, 4000);
  }

  function lampQuantity(block) {
    var section = block.closest('[data-section-type="product"]');
    var input = section ? section.querySelector('input[name="quantity"]') : null;
    return input ? (parseInt(input.value, 10) || 1) : 1;
  }

  /* PDP: running total when bulbs are ticked */
  function updateTotal(block) {
    var totalEl = block.querySelector('[data-bulb-upsell-total]');
    var amountEl = block.querySelector('[data-bulb-upsell-total-amount]');
    if (!totalEl || !amountEl) { return; }
    var checked = block.querySelectorAll('input[name="bulb-upsell-variant"]:checked');
    if (checked.length === 0) {
      totalEl.hidden = true;
      return;
    }
    var lampPrice = parseInt(block.getAttribute('data-lamp-price'), 10) || 0;
    var lampQty = lampQuantity(block);
    var total = lampPrice * lampQty;
    for (var i = 0; i < checked.length; i++) {
      total += (parseInt(checked[i].getAttribute('data-line-price'), 10) || 0) * lampQty;
    }
    amountEl.textContent = formatMoney(total);
    totalEl.hidden = false;
  }

  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!input) { return; }
    var block = input.closest ? input.closest('[data-bulb-upsell="pdp"]') : null;
    if (block && input.name === 'bulb-upsell-variant') {
      updateTotal(block);
      return;
    }
    if (input.name === 'quantity') {
      var pdp = document.querySelector('[data-bulb-upsell="pdp"]');
      if (pdp) { updateTotal(pdp); }
    }
  });

  /* Keep the lamp price current when the customer switches variant (Prestige dispatches variant:changed). */
  document.addEventListener('variant:changed', function (event) {
    var variant = event.detail && event.detail.variant;
    var block = document.querySelector('[data-bulb-upsell="pdp"]');
    if (!variant || !block) { return; }
    block.setAttribute('data-lamp-price', variant.price);
    updateTotal(block);
  });

  /* PDP: add ticked bulbs after the lamp was added by Prestige */
  document.addEventListener('product:added', function (event) {
    var section = event.target && event.target.querySelector ? event.target : document;
    var block = section.querySelector('[data-bulb-upsell="pdp"]');
    if (!block) { return; }
    var checked = block.querySelectorAll('input[name="bulb-upsell-variant"]:checked');
    if (checked.length === 0) { return; }
    var lampQty = (event.detail && parseInt(event.detail.quantity, 10)) || lampQuantity(block);
    var items = [];
    for (var i = 0; i < checked.length; i++) {
      items.push({
        id: parseInt(checked[i].value, 10),
        quantity: (parseInt(checked[i].getAttribute('data-quantity'), 10) || 1) * lampQty,
        properties: { _kilde: 'pdp' }
      });
    }
    addItems(items).then(function () {
      refreshCart();
    }).catch(function (error) {
      showError(block, error.message);
      refreshCart();
    });
  });

  /* PDP: "Køb nu" with a ticked bulb -> add lamp + bulb(s) to the cart, then go to checkout */
  document.addEventListener('click', function (event) {
    var button = event.target.closest ? event.target.closest('.shopify-payment-button__button') : null;
    if (!button) { return; }
    var section = button.closest('[data-section-type="product"]');
    var block = section ? section.querySelector('[data-bulb-upsell="pdp"]') : null;
    if (!block) { return; }
    var checked = block.querySelectorAll('input[name="bulb-upsell-variant"]:checked');
    if (checked.length === 0) { return; }
    var form = section.querySelector('form[action*="/cart/add"]');
    var variantInput = form ? form.querySelector('[name="id"]') : null;
    var variantId = variantInput ? parseInt(variantInput.value, 10) : 0;
    if (!variantId) { return; }
    event.preventDefault();
    event.stopImmediatePropagation();
    if (button.disabled) { return; }
    button.disabled = true;
    var lampQty = lampQuantity(block);
    var items = [{ id: variantId, quantity: lampQty }];
    for (var i = 0; i < checked.length; i++) {
      items.push({
        id: parseInt(checked[i].value, 10),
        quantity: (parseInt(checked[i].getAttribute('data-quantity'), 10) || 1) * lampQty,
        properties: { _kilde: 'pdp' }
      });
    }
    addItems(items).then(function () {
      window.location.href = ((window.routes && window.routes.rootUrlWithoutSlash) || '') + '/checkout';
    }).catch(function (error) {
      button.disabled = false;
      showError(block, error.message);
    });
  }, true);

  /* Cart drawer / cart page: "Læg i kurven" */
  document.addEventListener('click', function (event) {
    var button = event.target.closest ? event.target.closest('[data-bulb-upsell-add]') : null;
    if (!button) { return; }
    event.preventDefault();
    if (button.disabled) { return; }
    button.disabled = true;
    var card = button.closest('[data-bulb-upsell]');
    var items = [{
      id: parseInt(button.getAttribute('data-variant-id'), 10),
      quantity: parseInt(button.getAttribute('data-quantity'), 10) || 1,
      properties: { _kilde: button.getAttribute('data-source') || 'kurv' }
    }];
    addItems(items).then(function () {
      refreshCart();
    }).catch(function (error) {
      button.disabled = false;
      showError(card, error.message);
    });
  });
})();
